import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  const quoteId = searchParams.get('quote_id');
  const paymentTypeParam = searchParams.get('payment_type');

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      // Try quote payment status first (no auth needed), then fall back to authenticated endpoint
      let response;
      if (quoteId) {
        response = await axios.get(`${API}/payments/quote-status/${sessionId}`);
      } else {
        response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        toast.error('Payment session expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardContent className="py-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 animate-spin text-indigo-500 mx-auto mb-6" />
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Processing Payment
              </h2>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                {paymentTypeParam === 'plan' ? 'Deposit Payment Successful!' : 'Payment Successful!'}
              </h2>
              <p className="text-muted-foreground mb-8">
                {paymentTypeParam === 'plan' 
                  ? 'Your deposit has been received. View your live invoice to manage your remaining payments.'
                  : "Your event has been confirmed. We'll be in touch with next steps."
                }
              </p>
              <div className="flex flex-col gap-3">
                {paymentTypeParam === 'plan' && quoteId && (
                  <Button 
                    onClick={() => navigate(`/invoice/${quoteId}`)}
                    className="bg-indigo-500 hover:bg-indigo-600"
                    data-testid="view-invoice-btn"
                  >
                    View Live Invoice
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant={paymentTypeParam === 'plan' ? 'outline' : 'default'}
                  className={paymentTypeParam !== 'plan' ? 'bg-indigo-500 hover:bg-indigo-600' : ''}
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  {paymentTypeParam !== 'plan' && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-10 w-10 text-yellow-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Still Processing
              </h2>
              <p className="text-muted-foreground mb-8">
                Your payment is being processed. Please check your email for confirmation.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-border text-foreground"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Payment Session Expired
              </h2>
              <p className="text-muted-foreground mb-8">
                Please try again from your event page.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-border text-foreground"
              >
                Back to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
